import { useState } from 'react';
import { ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { cn } from '@ibas/ui';

interface StudentFilm {
  /** Short, factual title for the film. */
  title: string;
  /** One-line context (programme / role) shown under the title. */
  context: string;
  /** Real IBAS film (apps/web/public/media/videos). */
  video: string;
  /** Poster frame extracted from the film itself. */
  poster: string;
  /** Meaningful label for the film. */
  alt: string;
}

/**
 * Real IBAS films from the institution's own media library. Each is presented
 * with a factual title and context only — we do not attach quotes or personal
 * identities the footage doesn't itself establish (per the ibas-brand sourcing
 * rule). Posters are frames taken from each film.
 */
const STUDENT_FILMS: readonly StudentFilm[] = [
  {
    title: 'Why students love the IBAS MBA',
    context: 'MBA students',
    video: '/media/videos/why-students-love-ibas-mba.mp4',
    poster: '/media/videos/posters/why-students-love-ibas-mba.webp',
    alt: 'IBAS students during an MBA session.',
  },
  {
    title: 'The IBAS experience',
    context: 'Student story — Nguyen Hoang Anh Quoc',
    video: '/media/videos/nguyen-hoang-anh-quoc-student-experience.mp4',
    poster: '/media/videos/posters/nguyen-hoang-anh-quoc-student-experience.webp',
    alt: 'Title card from an IBAS student-experience film.',
  },
  {
    title: 'A DBA graduate testimonial',
    context: 'Flexi-DBA graduate',
    video: '/media/videos/dba-graduate-testimonial.mp4',
    poster: '/media/videos/posters/dba-graduate-testimonial.webp',
    alt: 'Still from an IBAS Doctor of Business Administration graduate testimonial.',
  },
  {
    title: 'Interview with Claes',
    context: 'The IBAS community',
    video: '/media/videos/interview-with-claes.mp4',
    poster: '/media/videos/posters/interview-with-claes.webp',
    alt: 'Still from an interview about studying at IBAS.',
  },
];

export interface StudentVideosProps {
  className?: string;
}

/**
 * StudentVideos — "Voices from IBAS" video showcase built from the institution's
 * own films.
 *
 * Motion intent: the eyebrow/heading rises in with a word-by-word SplitReveal,
 * then one large featured film sits beside its title and context. A switcher
 * (prev/next controls plus a title tablist) moves between the films; changing
 * film re-keys and re-reveals the story panel. The film is a real,
 * user-controlled <video> with native controls and sound — nothing autoplays, so
 * playback is always viewer-initiated (honouring the brand "no disruptive
 * autoplay" rule) while still letting a visitor hear the story. Under reduced
 * motion the heading renders instantly and the panel swaps without animation —
 * everything stays static and fully readable.
 *
 * The whole section is capped to one screen (min-h/justify-center).
 */
export function StudentVideos({ className }: StudentVideosProps) {
  const count = STUDENT_FILMS.length;
  const [active, setActive] = useState(0);
  const go = (i: number) => setActive(((i % count) + count) % count);
  const current = STUDENT_FILMS[active];

  return (
    <section
      aria-labelledby="student-videos-heading"
      className={cn(
        'flex min-h-[100svh] flex-col justify-center overflow-hidden bg-background px-6 py-12 lg:px-16',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Voices from IBAS
          </p>
          {/* SplitReveal doesn't forward `id`, so the heading id lives on this
              wrapper — aria-labelledby is satisfied by its text content. */}
          <div id="student-videos-heading">
            <SplitReveal
              as="h2"
              by="words"
              className="mt-3 font-display text-3xl font-semibold text-foreground sm:text-4xl lg:text-5xl"
            >
              Hear from our community
            </SplitReveal>
          </div>
        </div>

        <ScrollReveal className="mt-8 grid items-center gap-8 sm:mt-10 lg:grid-cols-[1.5fr_1fr] lg:gap-12">
          {/* Featured film — big, native controls + sound, no autoplay. Keyed on
              the active film so switching resets it to the new poster. */}
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <video
              key={current?.video}
              controls
              preload="none"
              playsInline
              poster={current?.poster}
              aria-label={`Film: ${current?.title}`}
              className="aspect-video w-full bg-brand-navy object-contain"
            >
              <source src={current?.video} type="video/mp4" />
            </video>
          </div>

          {/* Title + context + switcher */}
          <div className="flex flex-col">
            {/* Re-key so the panel fades in fresh on each switch (motion-reduce
                gated so opt-out users just see an instant swap). */}
            <figure
              key={active}
              className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-500"
            >
              <GraduationCap
                aria-hidden="true"
                className="mb-4 size-8 text-brand-gold"
                strokeWidth={1.5}
              />
              <h3 className="font-display text-2xl leading-snug font-medium text-foreground sm:text-3xl">
                {current?.title}
              </h3>
              <figcaption className="mt-6">
                <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  {current?.context}
                </p>
              </figcaption>
            </figure>

            {/* Switcher: prev/next flanking a title tablist. */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => go(active - 1)}
                aria-label="Previous film"
                className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors duration-300 ease-out hover:border-brand-gold hover:text-brand-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold"
              >
                <ChevronLeft className="size-5" aria-hidden strokeWidth={1.75} />
              </button>

              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Choose a film">
                {STUDENT_FILMS.map((film, i) => {
                  const isActive = i === active;
                  return (
                    <button
                      key={film.title}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-label={film.title}
                      onClick={() => go(i)}
                      className={cn(
                        'rounded-full border px-4 py-2 font-label text-xs tracking-[0.12em] uppercase transition-colors duration-300 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold',
                        isActive
                          ? 'border-brand-navy bg-brand-navy text-white'
                          : 'border-border bg-background text-muted-foreground hover:border-brand-gold hover:text-foreground',
                      )}
                    >
                      {film.title}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => go(active + 1)}
                aria-label="Next film"
                className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors duration-300 ease-out hover:border-brand-gold hover:text-brand-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold"
              >
                <ChevronRight className="size-5" aria-hidden strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
